import { Country } from '@shopify/address';
import axios, { AxiosError } from 'axios';
import { FC, useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { CUSTOMER_API_URL } from './constants';
import { Address } from './pages/Address';
import { BoxDetail } from './pages/BoxDetail';
import { SubscriptionAdd } from './pages/SubscriptionAdd';
import { SubscriptionCancel } from './pages/SubscriptionCancel';
import { SubscriptionDetailShokutasu } from './pages/SubscriptionDetailShokutasu';
import { SubscriptionLineEditProduct } from './pages/SubscriptionLineEditProduct';
import { SubscriptionLineEditVariantShokutasu } from './pages/SubscriptionLineEditVariantShokutasu';
import { SubscriptionList } from './pages/SubscriptionList';
import './styles/App.css';

export type Setting = {
  token: string;
  resize: (height: number) => void;
  referrer?: string;
  getScroll: () => { x: number; y: number };
  monitorScroll: () => void;
};

type SubscriptionStatus = 'ACTIVE' | 'PAUSED' | 'CANCELLED';

type SubscriptionDiscountsApiType = {
  amount: number;
  coupon: boolean;
  deliveryDiscount: boolean;
  percentage: number;
  rank: boolean;
  subscriptionManualDiscountId: string;
  title: string;
  targetType: string;
};

export type SubscriptionLine = {
  productId: string;
  variantId: string;
  variantImage?: string;
  title: string;
  onlineStorePreviewUrl: string;
  variantTitle: string;
  sku?: string;
  quantity: number;
  currentPriceAmount: number;
  currentPriceCurrencyCode: string;
  lineDiscountedPriceAmount: number;
  lineDiscountedPriceCurrencyCode: string;
  lineId: string;
  sellingPlanId?: string;
};

type BillingAttempt = {
  orderId?: string;
  orderToken?: string;
  orderName?: string;
  ready: boolean;
  createdAt: string;
  errorMessage?: string;
  errorCode?: string;
};

type SubscriptionHistory = {
  status: string;
  skipCount?: number;
  canceledAt: string;
  createdAt: string;
};

type RankGroup = {
  public: boolean;
};

type Shop = {
  showPause: boolean;
  showCancel: boolean;
  showSkip: boolean;
  showChangeAddress: boolean;
  mypageRemarks: string;
  showDelivery: boolean;
  deliveryDays: number;
  mypageChangeDeliveryDays: boolean;
  enableDeliveryTimesAM: boolean;
  enableDeliveryTimes12: boolean;
  enableDeliveryTimes14: boolean;
  enableDeliveryTimes16: boolean;
  enableDeliveryTimes18: boolean;
  enableDeliveryTimes19: boolean;
  enableDeliveryTimes20: boolean;
  disableDeliveryDaysMon: boolean;
  disableDeliveryDaysTue: boolean;
  disableDeliveryDaysWed: boolean;
  disableDeliveryDaysThr: boolean;
  disableDeliveryDaysFri: boolean;
  disableDeliveryDaysSat: boolean;
  disableDeliveryDaysSun: boolean;
  showChangeProduct: boolean;
  showNewAddProduct: boolean;
  showChangePayment: boolean;
  showRemoveProduct: boolean;
  showNewOrder: boolean;
  rankGroup: RankGroup;
  showChangeQuantity: boolean;
  maxSkipCount: number;
  hideLowestBuySkip: boolean;
  hideLowestBuyPause: boolean;
  hideLowestBuyCancel: boolean;
  showPlanModify: boolean;
  isAllowModifyDays: boolean;
  allowModifyDays: number;
  hideLowestModify: boolean;
  showStockAlert: boolean;
  deliveryDaysMax: number;
  changeNextBillingDateWithDeliveryDate: boolean;
  enableDeliveryDateNoDay: boolean;
  deliveryTimes: { tag: string; title: string }[];
  businessHolidays: { id: string; targetDate: string; shopId: string; createdAt: string }[];
};

type Rank = {
  name: string;
  discountPercentage: number;
};

export type Subscription = {
  customerId: string;
  originOrderId: string;
  originOrderToken: string;
  originOrderName: string;
  customerDisplayName: string;
  customerEmail: string;
  customerPaymentMethodId?: string;
  nextBillingDate: Date;
  nextDeliveryDate: Date;
  subscriptionContractId: string;
  billingPolicyInterval: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
  billingPolicyIntervalCount: number;
  billingPolicyMinCycles: number;
  billingPolicyMaxCycles: number;
  status: SubscriptionStatus;
  subscriptionLines: SubscriptionLine[];
  subscriptionHistories: SubscriptionHistory[];
  subscriptionDiscounts: SubscriptionDiscountsApiType[];
  billingAttempts: BillingAttempt[];
  deliveryPriceAmount: number;
  deliveryPriceCurrencyCode: string;
  deliveryAddress1: string;
  deliveryAddress2: string;
  deliveryCity: string;
  deliveryCompany: string;
  deliveryCountry: string;
  deliveryCountryCode: string;
  deliveryFirstName: string;
  deliveryLastName: string;
  deliveryName: string;
  deliveryPhone: string;
  deliveryProvince: string;
  deliveryProvinceCode: string;
  deliveryZip: string;
  deliveryDays: number;
  deliveryTime: string;
  deliveryTimeText: string;
  createdAt: string;
  disabled: boolean;
  deliveryFormattedAddress: string[];
  countries: Country[];
  country: Country;
  paymentType: string;
  shop: Shop;
  rank: Rank;
};

export const statusMessages = {
  ACTIVE: 'アクティブ',
  PAUSED: '一時停止',
  CANCELLED: '解約',
  EXPIRED: '期限切れ',
  FAILED: '失敗',
};
const History: FC<{ children: any }> = ({ children }) => {
  const pathname = useLocation().pathname;

  useEffect(() => {
    window.parent.postMessage({ action: 'pushHistory', path: pathname }, '*');
    window.postMessage('stopMonitor', '*');
  }, [pathname]);

  return children;
};

export const deliveryTimesConstants: { value: string; label: string }[] = [
  { value: 'AM', label: '午前中' },
  { value: '12', label: '12:00〜14:00' },
  { value: '14', label: '14:00〜16:00' },
  { value: '16', label: '16:00〜18:00' },
  { value: '18', label: '18:00〜20:00' },
  { value: '19', label: '19:00〜21:00' },
  { value: '20', label: '20:00〜21:00' },
];

export const getModalOption = (setting: Setting) => {
  return {
    onShow: () => {
      const dialog = document.querySelector('.micromodal.is-open [role="dialog"]') as HTMLDivElement;
      const body = document.querySelector('body');
      if (!dialog || !body) {
        return;
      }

      console.log({ setting });

      const scroll = setting.getScroll();
      let pos = scroll.y;

      console.log({ pos });

      console.log(body.clientHeight);

      if (body.clientHeight < pos + dialog.clientHeight) {
        pos = body.clientHeight - dialog.clientHeight - 10;
      } else if (pos < 10) {
        pos = 10;
      }

      dialog.style.top = `${pos}px`;
    },
  };
};

export const App = () => {
  const [config, setConfig] = useState<{
    token: string;
    styleTexts: string[];
    styleUrls: string[];
  }>();

  let scrollX = 0,
    scrollY = 0;

  const resize = (height: number) => {
    window.parent.postMessage({ action: 'resetWindow', height }, '*');
  };

  const getScroll = () => {
    return {
      x: scrollX,
      y: scrollY,
    };
  };

  let scrollTracking = false;
  let frameId = 0;
  const onScroll = async (e: MessageEvent) => {
    if (frameId && e.data === 'stopMonitor') {
      window.removeEventListener('message', onScroll);
      return;
    }

    if (scrollTracking || e.data.pageYOffset === undefined || e.data.pageXOffset === undefined) {
      return;
    }

    if (frameId) {
      cancelAnimationFrame(frameId);
      frameId = 0;
      scrollTracking = false;
    }

    frameId = requestAnimationFrame(() => {
      scrollX = e.data.pageXOffset;
      scrollY = e.data.pageYOffset;

      scrollTracking = false;
    });

    scrollTracking = true;
  };

  const monitorScroll = () => {
    window.addEventListener('message', onScroll);
  };

  // Ref: https://github.com/mdn/translated-content/blob/main/files/ja/web/api/web_storage_api/using_the_web_storage_api/index.html#L54
  const storageAvailable = () => {
    var storage;
    try {
      storage = window.localStorage;
      var x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    } catch (e) {
      return (
        e instanceof DOMException &&
        // everything except Firefox
        (e.code === 22 ||
          // Firefox
          e.code === 1014 ||
          // test name field too, because code might not be present
          // everything except Firefox
          e.name === 'QuotaExceededError' ||
          // Firefox
          e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
        // acknowledge QuotaExceededError only if there's something already stored
        storage &&
        storage.length !== 0
      );
    }
  };

  const loggedIn = (shopId: string, customerId: string) => {
    const shopTokenStr = localStorage.getItem(shopId);
    if (!shopTokenStr) return false;

    const shopToken = JSON.parse(shopTokenStr);

    return (
      shopToken.jwt &&
      shopToken.customerId === customerId &&
      shopToken.jwtExpiredTimestamp &&
      Number(shopToken.jwtExpiredTimestamp) >= new Date().getTime()
    );
  };

  const initializeEvent = (event: MessageEvent) => {
    // We can't rejected by event.origin because Shopify Store domain is not unique.
    // So, We check data structure only.
    const { code, styleTexts, styleUrls, shopId, customerId } = event.data;
    if (code === undefined || !styleTexts || !styleUrls || !shopId) {
      return;
    }

    if (storageAvailable() && loggedIn(shopId, customerId)) {
      const shopTokenStr = localStorage.getItem(shopId);
      const shopToken = JSON.parse(shopTokenStr!);

      setConfig({ token: shopToken.jwt, styleTexts, styleUrls });

      return;
    }

    axios
      .post(`${CUSTOMER_API_URL}?action=token`, {
        code,
      })
      .then((response) => {
        window.removeEventListener('message', initializeEvent);

        const token = response.data;
        const jwtExpiredTimestamp = new Date().getTime() + 60 * 60 * 1000; // 1 hour

        if (storageAvailable()) {
          const shopToken = {
            jwt: token,
            customerId,
            jwtExpiredTimestamp: jwtExpiredTimestamp,
          };
          localStorage.setItem(shopId, JSON.stringify(shopToken));
          console.log('stored localStorage');
        }

        setConfig({ token, styleTexts, styleUrls });
      })
      .catch((error: AxiosError) => {
        if (!error.response) {
          // Debug for now
          throw new Error(`Network Error but not response: ${error.message}`);
        }

        if (error.response.status >= 500) {
          throw error;
        }
      });
  };

  useEffect(() => {
    window.addEventListener('message', initializeEvent);

    window.addEventListener('resize', () => {
      // Safari doesn't compute "float: left" height.
      if (!document.querySelector('body')!.scrollHeight) {
        return;
      }

      // 解約ページのバッファー変更
      let buffer = 50;
      if (window.location.href.indexOf('cancel') > -1) {
        buffer = 10;
      }

      const height = document.querySelector('body')!.scrollHeight + buffer; // Buffer

      resize(height);
    });

    window.parent.postMessage({ action: 'initialize' }, '*');
  }, []);

  if (!config) {
    return <>認証中です。少々お待ちください。</>;
  }

  const setting = {
    token: config.token,
    resize,
    referrer: document.referrer,
    getScroll,
    monitorScroll,
  };

  config.styleTexts.forEach((styleText) => {
    const style = document.createElement('style');
    style.innerText = styleText;

    document.body.appendChild(style);
  });

  config.styleUrls.forEach((url) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;

    document.body.appendChild(link);
  });

  return (
    <BrowserRouter basename="">
      <History>
        <Routes>
          <Route path="/subscription/add" element={<SubscriptionAdd setting={setting} />} />
          <Route path="/subscriptions/:id/cancel" element={<SubscriptionCancel setting={setting} />} />
          <Route
            path="/subscriptions/:id/products/:productId/lines/:lineId"
            element={<SubscriptionLineEditVariantShokutasu setting={setting} />}
          />
          <Route path="/subscriptions/:id/box/:boxId" element={<BoxDetail setting={setting} />} />
          <Route path="/subscriptions/:id/lines/:lineId" element={<SubscriptionLineEditProduct setting={setting} />} />
          <Route path="/subscriptions/:id/edit" element={<Address setting={setting} />} />
          <Route path="/subscriptions/:id" element={<SubscriptionDetailShokutasu setting={setting} />} />
          <Route path="/" element={<SubscriptionList setting={setting} />} />
        </Routes>
      </History>
    </BrowserRouter>
  );
};
